import React from 'react';
import './query-message-list.scss';
import { MessageListItem } from '../../../types';
import { isDateSeparatorItem, isQuerySeparatorItem } from '../../helper';
import { displayDateTime } from '@zonofi/common/functions';
import {
  TemplateComponents,
  useGetConversationMessage,
} from '../../../conversations';
import { getChannel, useAppSelector } from '@zonofi/zono-money-store';
import { useRenderTemplateUi } from '@zonofi/common/hooks/render-template-ui';

interface QueryMessageListProps {
  messageItem: MessageListItem;
  messageItemKey: string;
  paginationAnchorMessageId?: string;
  lastItemRef: React.MutableRefObject<HTMLDivElement | null>;
  growSeparatorElementsRef: React.MutableRefObject<Map<string, HTMLDivElement>>;
  messageElementsRef: React.MutableRefObject<Map<string, HTMLDivElement>>;
  scrollToItem: (msgId: string) => void;
}

export const QueryMessageList: React.FC<QueryMessageListProps> = ({
  messageItem,
  messageItemKey,
  paginationAnchorMessageId,
  lastItemRef,
  growSeparatorElementsRef,
  messageElementsRef,
  scrollToItem,
}) => {
  const channel = useAppSelector(getChannel);

  if (isDateSeparatorItem(messageItem)) {
    return (
      <div
        className="query-message__container-view"
        style={{ backgroundColor: 'transparent' }}
      >
        <div className="query-message__date-view font-regular">
          {displayDateTime(messageItem.dayKey, 'date')}
        </div>
      </div>
    );
  }

  if (isQuerySeparatorItem(messageItem)) {
    const { boundary, backgroundColor, queryType } = messageItem;
    if (boundary === 'end') {
      return (
        <div
          className="query-message__container-view"
          style={{ backgroundColor: `${backgroundColor}1f` }}
        >
          <div
            className="query-message__separator-end"
            style={{ color: backgroundColor }}
          />
        </div>
      );
    }

    return (
      <div
        ref={(node) => {
          if (node) {
            growSeparatorElementsRef.current.set(messageItemKey, node);
          } else {
            growSeparatorElementsRef.current.delete(messageItemKey);
          }
        }}
        className="query-message__container-view"
        style={{ backgroundColor: `${backgroundColor}1f` }}
      >
        <div
          className="query-message__separator-start"
          style={{ color: backgroundColor }}
        >
          <div
            className="query-message__chip font-regular"
            style={{
              backgroundColor: `${backgroundColor}1f`,
              borderColor: backgroundColor,
            }}
          >
            {queryType}
          </div>
        </div>
      </div>
    );
  }

  const isPaginationAnchor =
    messageItem.message?._id !== undefined &&
    messageItem.message?._id !== null &&
    String(messageItem.message._id) === paginationAnchorMessageId;

  const messageId =
    messageItem.message?._id !== undefined && messageItem.message?._id !== null
      ? String(messageItem.message._id)
      : undefined;

  const {
    userName,
    msgTime,
    template,
    msgId,
    generateFileUrlPayload,
    msgType,
    isOwn,
  } = useGetConversationMessage({
    message: messageItem?.message,
    timetoken: String(messageItem?.timetoken),
    channel,
  });

  const renderedComponents = useRenderTemplateUi({
    messageId: msgId,
    template,
    templateUI: TemplateComponents,
    generateFileUrlPayload,
    msgType,
  });

  return (
    <div
      data-key={messageId}
      style={{
        backgroundColor:
          messageItem?.messageListBucket?.type === 'query'
            ? `${messageItem?.messageListBucket?.backgroundColor}1f`
            : 'transparent',
        padding: '3px 0',
      }}
      ref={(node) => {
        if (messageId !== undefined && messageId !== null) {
          if (node) {
            messageElementsRef.current.set(messageId, node);
          } else {
            messageElementsRef.current.delete(messageId);
          }
        }

        if (isPaginationAnchor) {
          lastItemRef.current = node;
        }
      }}
    >
      <div
        className="query-message-card"
        style={{
          maxWidth: '60%',
          backgroundColor: isOwn ? '#EBF6FD' : '#fff',
          borderBottomLeftRadius: isOwn ? '8px' : 'none',
          borderBottomRightRadius: isOwn ? 'none' : '8px',
          marginLeft: isOwn ? 'auto' : '10px',
          marginRight: isOwn ? '10px' : 'auto',
        }}
      >
        {!isOwn && (
          <div className="query-message-card__user-name font-semibold">
            {userName}
          </div>
        )}

        {renderedComponents}

        <div className="query-message-card__msg-time font-regular">
          {msgTime}
        </div>
      </div>
    </div>
  );
};
